import { useFormContext } from 'react-hook-form'

import { useDeleteSiteDetails, useGetSiteDetails } from '@apps-common/apis/portal/site-details'
import { useGetUnitPlans } from '@apps-common/apis/portal/unit'
import {
  ActivationCompanyView,
  ActivationExistingUnitView,
  ActivationPlanView,
  ActivationSiteView,
  ActivationUnitView
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components/activation-confirmation'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { MESSAGE_GENERIC_ERROR } from '@apps-common/config/app-messages'
import { commonIcons } from '@apps-common/config/icons'
import { toastError } from '@apps-common/lib/toast'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'

interface IProps {
  loading: boolean
  updating: boolean
  step: ModifyUnitActivationStep
  moveStep: (direction: stepDirection) => void
}

export const UnitActivationConfirmationForm = ({ loading, updating, step, moveStep }: IProps) => {
  const { getValues } = useFormContext<IActivationServiceRequestCreateModel>()

  const { productId, unitId, unitPlanId, existingUnitId } = getValues()

  const isStepReview = step === ModifyUnitActivationStep.confirm

  const {
    data: plans,
    isLoading: plansIsLoading,
    isError: plansIsError
  } = useGetUnitPlans({
    config: {
      enabled: isStepReview && productId?.length > 0
    },
    productId
  })

  const { mutateAsync: deleteAsync, isPending: deleteIsPending } = useDeleteSiteDetails()

  const {
    refetch: refetchSiteDetails,
    isLoading: siteIsLoading,
    isError: siteIsError,
    data: site
  } = useGetSiteDetails({
    config: {
      retry: false,
      staleTime: 0,
      enabled: isStepReview && unitId?.length > 0
    },
    unitId
  })

  const clearSiteDetails = async () => {
    try {
      await deleteAsync({
        id: site?.siteDetailsId || 0
      })
      refetchSiteDetails()
    } catch (e) {
      toastError(MESSAGE_GENERIC_ERROR)
    }
  }

  const plan = plans?.find((p) => p.unitPlanId === unitPlanId)

  loading = loading || !plan || plansIsLoading || plansIsError || siteIsLoading
  updating = updating || deleteIsPending

  const companyView = <ActivationCompanyView loading={loading} updating={updating} />
  const unitView = <ActivationUnitView loading={loading} updating={updating} />
  const existingUnitView = <ActivationExistingUnitView loading={loading} updating={updating} />
  const planView = <ActivationPlanView loading={loading} updating={updating} plan={plan} />
  const siteView = (
    <ActivationSiteView
      loading={loading}
      updating={updating}
      site={site}
      error={siteIsError}
      refetch={refetchSiteDetails}
      clear={clearSiteDetails}
    />
  )

  return (
    <CollapsibleContent
      title='Step 3 | Confirmation'
      defaultOpen={step === ModifyUnitActivationStep.confirm}
      disabled={step !== ModifyUnitActivationStep.confirm}
      classes='bg-surface border border-border rounded-2xl text-text'
    >
      <div className='sm:grid sm:grid-cols-2 sm:gap-x-[30px] hidden'>
        <div>
          {companyView}
          {unitView}
          {!!existingUnitId && existingUnitView}
        </div>
        <div>
          {planView}
          {siteView}
        </div>
      </div>
      <div className='sm:hidden sm:grid-cols-2 sm:gap-x-[30px] block'>
        {companyView}
        {unitView}
        {!!existingUnitId && existingUnitView}
        {planView}
        {siteView}
      </div>
      <div className='flex items-center justify-end gap-[10px]'>
        <ButtonRoundedSm
          type='button'
          loading={loading || updating}
          text='Previous'
          className='sm:grow-0 action-neutral'
          icon={<commonIcons.arrowAngledLeft />}
          callback={() => moveStep('backward')}
        />
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Save'
          iconRight
          className='sm:grow-0 action-success'
          icon={<commonIcons.arrowAngled />}
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
